import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { Check } from 'lucide-react';

import type { Configuracao } from '@/api/secretario';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import { AdvogadosStep } from './AdvogadosStep';
import { AdvogadoStep } from './AdvogadoStep';
import { AreasAtendidasStep } from './AreasAtendidasStep';
import { AtivacaoStep } from './AtivacaoStep';
import { CanalStep } from './CanalStep';
import { IdentidadeStep } from './IdentidadeStep';
import { WizardSaveProvider, type RegisterFlush } from './WizardSaveContext';

interface SecretarioWizardProps {
  config: Configuracao | null;
  /**
   * Leva ao passo de chave ``chave`` (ex.: ``canal``, vindo do aviso de desconexão).
   * Cada pedido novo traz um ``seq`` novo; ir ao passo salva o que estava pendente.
   */
  irParaPasso?: { chave: string; seq: number } | null;
  /**
   * Passo em que o wizard abre. A página o guarda (via ``onPassoChange``) porque o
   * wizard REMONTA quando a configuração é criada — sai do estado vazio para dentro
   * das abas — e sem isso o primeiro "Salvar e continuar" voltava ao passo 1.
   */
  passoInicial?: string | null;
  onPassoChange?: (chave: string) => void;
  /** Na criação: de quem é o secretário, já escolhido no seletor da página. */
  escopoInicial?: string | null;
}

type StepComponent = (props: Parameters<typeof IdentidadeStep>[0]) => ReactElement | null;

interface StepDef {
  key: string;
  label: string;
  Component: StepComponent;
  /** Passo concluído? Deriva da prontidão/estado vindos do backend. */
  completo: (config: Configuracao | null) => boolean;
}

const temPendencia = (config: Configuracao, ...chaves: string[]) =>
  config.prontidao.pendencias.some((p) => chaves.includes(p.chave));

// Honorários, modelos e perguntas (o que faz o Secretário ir além do
// pré-atendimento) ficam DENTRO da aba Áreas, no modo "Personalizar áreas", e são
// opcionais — era no passo que os exigia que o cadastro morria
// (docs/PLANO_SECRETARIO_ATIVACAO_SIMPLES.md).
const STEPS: StepDef[] = [
  {
    key: 'identidade',
    // "Identidade" descrevia só a metade de cima do passo. Ele responde "quem é o
    // secretário e como ele trabalha"; o passo seguinte responde "quem é você".
    label: 'O Secretário',
    Component: IdentidadeStep,
    completo: (c) => !!c && !!c.nome_exibicao,
  },
  {
    key: 'areas',
    label: 'Áreas',
    Component: AreasAtendidasStep,
    completo: (c) => !!c && !temPendencia(c, 'sem_area'),
  },
  {
    key: 'advogado',
    label: 'Seus dados',
    Component: AdvogadoStep,
    completo: (c) => !!c && !temPendencia(c, 'certificado', 'oab'),
  },
  {
    key: 'canal',
    label: 'Número',
    Component: CanalStep,
    completo: (c) => !!c && !temPendencia(c, 'canal', 'canal_pendente'),
  },
  {
    key: 'ativacao',
    label: 'Ativação',
    Component: AtivacaoStep,
    completo: (c) => !!c && c.ativo,
  },
];

/**
 * O do escritório (docs/PLANO_SECRETARIO_ESCRITORIO.md): os advogados no lugar de
 * "Áreas" e "Seus dados" — as áreas, os honorários e os modelos são de cada um,
 * no perfil dele; OAB e certificado são do cadastro de cada advogado.
 */
const STEPS_ESCRITORIO: StepDef[] = [
  STEPS[0],
  {
    key: 'advogados',
    label: 'Advogados',
    Component: AdvogadosStep,
    completo: (c) => !!c && !temPendencia(c, 'sem_participante', 'sem_area'),
  },
  STEPS[3],
  STEPS[4],
];

/**
 * Wizard guiado de configuração/ativação. Sem configuração ainda, fica preso no
 * passo 1 (criar); criada, a navegação entre passos é livre (clicar no indicador
 * ou usar Voltar/Continuar). A conclusão de cada passo vem da prontidão do backend.
 *
 * Todos os passos ficam MONTADOS (os inativos escondidos): navegar não desmonta o
 * passo atual, então edição não salva nunca é descartada. Além disso, ao navegar o
 * wizard dispara os saves pendentes registrados pelos passos (auto-save,
 * fire-and-forget — salvar nunca bloqueia a navegação).
 */
export function SecretarioWizard({
  config,
  irParaPasso,
  passoInicial,
  onPassoChange,
  escopoInicial,
}: SecretarioWizardProps) {
  const passos = config?.escopo === 'ESCRITORIO' ? STEPS_ESCRITORIO : STEPS;
  const [step, setStep] = useState(() =>
    Math.max(0, passos.findIndex((s) => s.key === (passoInicial ?? irParaPasso?.chave))),
  );
  const idx = config ? Math.min(step, passos.length - 1) : 0;
  const podeNavegar = config != null;

  const flushesRef = useRef(new Map<string, () => void>());
  const registerFlush = useCallback<RegisterFlush>((key, flush) => {
    if (flush) {
      flushesRef.current.set(key, flush);
    } else {
      flushesRef.current.delete(key);
    }
  }, []);
  const irPara = useCallback(
    (i: number) => {
      flushesRef.current.forEach((flush) => flush());
      setStep(i);
      onPassoChange?.(passos[i].key);
    },
    [onPassoChange, passos],
  );

  // Pedido de fora (o "Reconectar" da página): vai ao passo pelo mesmo caminho
  // da navegação, que salva o que estava pendente. Fora do passo síncrono do effect.
  const chavePedida = irParaPasso?.chave;
  const seqPedido = irParaPasso?.seq;
  useEffect(() => {
    const destino = passos.findIndex((s) => s.key === chavePedida);
    if (destino < 0) return undefined;
    const timer = window.setTimeout(() => irPara(destino), 0);
    return () => window.clearTimeout(timer);
  }, [chavePedida, seqPedido, irPara, passos]);

  return (
    <div className="space-y-6">
      {/* No celular, só as bolinhas numa linha e o nome da etapa atual embaixo (como no
          cadastro de cliente): as cinco etapas com nome ocupavam três linhas. */}
      <ol className="flex gap-2 sm:flex-wrap">
        {passos.map((s, i) => {
          const concluido = s.completo(config);
          const atual = i === idx;
          const habilitado = podeNavegar || i === 0;
          return (
            <li key={s.key} className="flex-1">
              <Button
                type="button"
                variant="ghost"
                disabled={!habilitado}
                onClick={() => irPara(i)}
                aria-current={atual ? 'step' : undefined}
                className={cn(
                  // O passo atual se distingue pelo FUNDO, não por borda colorida:
                  // cinco chips lado a lado com contorno vermelho gritam mais que o
                  // conteúdo da própria tela. A borda fica neutra em todos.
                  'flex h-11 w-full items-center justify-center gap-2 rounded-lg border px-0 text-left sm:h-auto sm:justify-start sm:px-3 sm:py-2',
                  atual && 'bg-primary/10',
                )}
              >
                <span
                  className={cn(
                    'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                    concluido
                      ? 'bg-emerald-500 text-white'
                      : atual
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground',
                  )}
                >
                  {concluido ? <Check size={14} /> : i + 1}
                </span>
                <span className="truncate text-sm font-medium max-sm:sr-only">{s.label}</span>
              </Button>
            </li>
          );
        })}
      </ol>
      <p className="-mt-3 text-center text-sm text-muted-foreground sm:hidden">
        Etapa {idx + 1} de {passos.length} — {passos[idx].label}
      </p>

      <WizardSaveProvider value={registerFlush}>
        {passos.map(({ key, Component }, i) => (
          <div key={key} hidden={i !== idx}>
            <Component
              config={config}
              escopoInicial={escopoInicial}
              isFirst={i === 0}
              onBack={() => irPara(Math.max(0, i - 1))}
              onNext={() => irPara(Math.min(passos.length - 1, i + 1))}
            />
          </div>
        ))}
      </WizardSaveProvider>
    </div>
  );
}
